import { getSession, signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import React, { useState } from 'react';

import {
  Alert,
  AlertDescription,
} from '@/components/common/feedback/alert/alert';
import { Button } from '@/components/common/inputs/button/button';
import { Input } from '@/components/common/inputs/text/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/common/overlay/dialog/dialog';
import { authService } from '@/lib/services';
import { userService } from '@/lib/services/user.service';

interface ConfirmationDialogProps {
  open: boolean;
  email: string;
  password: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function ConfirmationDialog({
  open,
  email,
  password,
  onClose,
  onSuccess,
}: ConfirmationDialogProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setError('Please enter the confirmation code');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      await authService.confirmSignUp(email, code.trim());
      setSuccessMessage('Account confirmed successfully');

      // Get step 2 data from session storage
      const step2DataStr = sessionStorage.getItem('registrationStep2Data');
      let step2Data = null;
      if (step2DataStr) {
        try {
          step2Data = JSON.parse(step2DataStr);
        } catch (e) {
          console.error('Error parsing step 2 data:', e);
        }
      }

      // After successful confirmation, attempt to login again
      const result = await signIn('credentials', {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        throw new Error(result.error);
      }

      // If we have step 2 data, update the user profile
      if (step2Data) {
        try {
          const session = await getSession();
          if (session?.user?.id) {
            await userService.updateUser(
              session.user.id,
              {
                city: step2Data.city,
                ageGroup: step2Data.ageGroup,
                language: step2Data.language,
                acceptedPolicies: step2Data.acceptedPolicies,
              },
              session
            );
          }
        } catch (error) {
          console.error('Error updating user profile:', error);
        }
      }

      // Clear step 2 data from session storage
      sessionStorage.removeItem('registrationStep2Data');

      onSuccess();
      router.push('/');
    } catch (error) {
      console.error('Confirmation error:', error);
      if (error instanceof Error) {
        if (error.message.includes('Too many attempts')) {
          setError(
            'Too many attempts. Please wait a few minutes before trying again.'
          );
        } else {
          setError(error.message);
        }
      } else {
        setError('An error occurred during confirmation. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setIsSubmitting(true);
      setError('');
      setSuccessMessage('');
      await authService.resendConfirmationCode(email);
      setSuccessMessage('A new confirmation code has been sent to your email.');
      setResendDisabled(true);
      // Re-enable the resend button after 30 seconds
      setTimeout(() => {
        setResendDisabled(false);
      }, 30000);
    } catch (error) {
      console.error('Error resending code:', error);
      if (error instanceof Error) {
        if (error.message.includes('Too many')) {
          setError(
            'Too many code resend attempts. Please wait a few minutes before trying again.'
          );
        } else {
          setError(error.message);
        }
      } else {
        setError('Failed to resend confirmation code. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm Your Account</DialogTitle>
          <DialogDescription>
            Please enter the confirmation code sent to {email}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className='space-y-4'>
          {error && (
            <Alert variant='destructive'>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {successMessage && (
            <Alert>
              <AlertDescription>{successMessage}</AlertDescription>
            </Alert>
          )}

          <Input
            autoFocus
            placeholder='Confirmation Code'
            type='text'
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setError('');
            }}
            disabled={isSubmitting}
          />

          <DialogFooter className='flex justify-between'>
            <Button
              type='button'
              variant='outline'
              onClick={handleResendCode}
              disabled={isSubmitting || resendDisabled}
            >
              {resendDisabled ? 'Wait 30s to Resend' : 'Resend Code'}
            </Button>
            <div className='space-x-2'>
              <Button
                type='button'
                variant='outline'
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={isSubmitting}>
                {isSubmitting ? 'Confirming...' : 'Confirm'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
